// ─── Telemetry utility for click/interaction tracking ────────────────────────
// Privacy-first: no identifiers, no fingerprinting, no cookies, no localStorage

import { isAnalyticsAllowed } from './consentGate';

const API_BASE = window.REACT_APP_DEMO_REQUESTS_API || '';

// Session hash cache (computed once per tab session)
let cachedSessionHash = null;
let sessionHashPromise = null;

/**
 * Get or generate session hash from sessionStorage.
 * Uses SHA-256 hash of a random UUID stored in sessionStorage.
 * Cached for the lifetime of the page to ensure synchronous access for sendBeacon.
 */
async function getSessionHash() {
  // Return cached hash if available
  if (cachedSessionHash) {
    return cachedSessionHash;
  }

  // Return existing promise if hash is being computed
  if (sessionHashPromise) {
    return sessionHashPromise;
  }

  // Compute hash asynchronously
  sessionHashPromise = (async () => {
    try {
      const STORAGE_KEY = 'ciq_session_id';
      let rawSessionId = null;

      // Try to get existing session ID from sessionStorage
      try {
        rawSessionId = sessionStorage.getItem(STORAGE_KEY);
      } catch (e) {
        // sessionStorage not available (private mode, etc.)
        return null;
      }

      // Generate new session ID if not found
      if (!rawSessionId) {
        rawSessionId = crypto.randomUUID();
        try {
          sessionStorage.setItem(STORAGE_KEY, rawSessionId);
        } catch (e) {
          // Failed to store, continue anyway
        }
      }

      // Hash the session ID using SHA-256
      const encoder = new TextEncoder();
      const data = encoder.encode(rawSessionId);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      
      // Convert ArrayBuffer to lowercase hex string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      cachedSessionHash = hashHex;
      return hashHex;
    } catch (err) {
      // If hashing fails, return null (session_hash will be omitted)
      return null;
    }
  })();

  return sessionHashPromise;
}

// Eagerly initialize session hash on module load
if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
  getSessionHash().catch(() => {
    // Silently fail - hash will be computed on first telemetry call
  });
}

// Debounce map: key = button + question_id + page_path, value = timestamp
const debounceMap = new Map();
const DEBOUNCE_MS = 2000;

function getDebounceKey(button, questionId, pagePath) {
  return `${button}:${questionId || ''}:${pagePath || ''}`;
}

function isDebounced(button, questionId, pagePath) {
  const key = getDebounceKey(button, questionId, pagePath);
  const lastSent = debounceMap.get(key);
  if (!lastSent) return false;
  const now = Date.now();
  if (now - lastSent < DEBOUNCE_MS) return true;
  debounceMap.set(key, now);
  return false;
}

function markSent(button, questionId, pagePath) {
  const key = getDebounceKey(button, questionId, pagePath);
  debounceMap.set(key, Date.now());
}

// Clean up old debounce entries periodically (every 5 minutes)
setInterval(() => {
  const now = Date.now();
  for (const [key, timestamp] of debounceMap) {
    if (now - timestamp > DEBOUNCE_MS * 10) {
      debounceMap.delete(key);
    }
  }
}, 5 * 60 * 1000);

/**
 * Send a telemetry click event.
 * Uses sendBeacon if available, otherwise fetch with keepalive.
 * Never blocks navigation.
 *
 * @param {string} button - Button identifier (required, <= 128 chars)
 * @param {object} context - Optional context object (<= 2KB when stringified)
 * @param {string} questionId - Optional question ID for debouncing (for accordion)
 */
export function trackClick(button, context = {}, questionId = null) {
  if (!isAnalyticsAllowed()) return;

  if (!button || typeof button !== 'string' || button.length > 128) {
    console.warn('[telemetry] Invalid button:', button);
    return;
  }

  const pagePath = window.location.pathname; // Always pathname only, no query params

  // Debounce check
  if (isDebounced(button, questionId, pagePath)) {
    return; // Silently ignore duplicate events
  }

  // Send telemetry with session hash
  // If hash is cached, use it synchronously; otherwise send without it
  const sendTelemetry = (sessionHash) => {
    const payload = {
      button: button.trim(),
      context: context || {},
      page_path: pagePath
    };

    // Include session_hash if available
    if (sessionHash) {
      payload.session_hash = sessionHash;
    }

    const payloadStr = JSON.stringify(payload);
    const url = `${API_BASE}/api/telemetry/click`;

    // Use sendBeacon if available (fire-and-forget, doesn't block navigation)
    if (navigator.sendBeacon) {
      try {
        const blob = new Blob([payloadStr], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) {
          markSent(button, questionId, pagePath);
          return;
        }
      } catch (err) {
        // Fall through to fetch if sendBeacon fails
      }
    }

    // Fallback to fetch with keepalive (doesn't block navigation)
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payloadStr,
      keepalive: true
    }).catch(() => {
      // Silently fail - telemetry should never break user experience
    });

    markSent(button, questionId, pagePath);
  };

  // If hash is already cached, use it synchronously
  if (cachedSessionHash) {
    sendTelemetry(cachedSessionHash);
    return;
  }

  // Otherwise, try to get it (may be async on first call)
  getSessionHash().then(sessionHash => {
    sendTelemetry(sessionHash);
  }).catch(() => {
    // If session hash fails, send without it
    sendTelemetry(null);
  });
}
